import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { GanttChartData, WorkTask } from "../../types/ganttChart";
import {
  SessionStatusResponse,
  TaskLocation,
  isShiftLocation,
} from "../../service/DragAndDropApi";

type TaskBoxConfigurations = {
  height: number;
  width: number;
  backgroundColor: string;
  border: string;
  allowedLines: number;
};

export type TaskBeingDraggedObject = {
  task: WorkTask;
  sourceLocation: TaskLocation;

  // Legacy field - kept for backward compatibility
  /** @deprecated Use 'sourceLocation' instead */
  shiftId: number | null;
};

// Helper function to create TaskBeingDraggedObject
export const createTaskBeingDraggedObject = (
  task: WorkTask,
  location: TaskLocation,
): TaskBeingDraggedObject => ({
  task,
  sourceLocation: location,
  // Maintain backward compatibility
  shiftId: isShiftLocation(location) ? location.shiftId : null,
});

export type IntersectingTaskInfo = {
  targetTaskId?: number;
  intersectingTaskId?: number;
  isCloserToStart: boolean;
  destinationTaskIdBefore: number | null;
  destinationTaskIdAfter: number | null;
};

export type IndicatorLine = {
  taskId: number;
  top: boolean;
};

type ScrollPosition = {
  left: number;
  top: number;
  zoomLevel?: number;
};

export interface DragAndDropSlice {
  yPosition?: number;
  xPosition?: number;
  isDragging: boolean;
  canDrop: boolean;
  taskBeingDragged?: TaskBeingDraggedObject;
  taskBoxConfigurtion?: TaskBoxConfigurations;
  intersectingIdTaskPlanAndPlacement?: IntersectingTaskInfo;
  isDragAndDropTabOpen: boolean;
  customGanttChartData?: GanttChartData;
  activeSession: boolean;
  rulesBroken?: number;
  isTransitioning: boolean;
  isDragConfirmed: boolean;
  isDragAndDropSessionWarningModalOpen?: boolean;
  indicatorLine?: IndicatorLine;
  scrollPosition?: ScrollPosition;
  activeSessionStatus?: SessionStatusResponse;
}

const initialState: DragAndDropSlice = {
  isDragging: false,
  canDrop: false,
  isDragAndDropTabOpen: false,
  activeSession: false,
  isTransitioning: false,
  isDragConfirmed: false,
};

const dragAndDropSSlice = createSlice({
  name: "drag-and-drop",
  initialState,
  reducers: {
    setIsDragging(state) {
      state.isDragging = true;
    },
    setTaskBoxConfigurations(
      state,
      action: PayloadAction<TaskBoxConfigurations>,
    ) {
      state.taskBoxConfigurtion = action.payload;
    },
    setCoordinates(state, action) {
      state.xPosition = action.payload.x;
      state.yPosition = action.payload.y;
    },
    setIntersectingIdTaskPlanAndPlacement(
      state,
      action: PayloadAction<IntersectingTaskInfo>,
    ) {
      const payload = action.payload;
      state.intersectingIdTaskPlanAndPlacement = payload;
      //Define where to place the blue indicator line.
      //TODO add indicatorline on unplanned.

      if (!payload.intersectingTaskId) {
        delete state.indicatorLine;
        return;
      }
      //If task intersects with original position and isCLoser is false, show indicator line on next task.
      if (
        payload.intersectingTaskId === state.taskBeingDragged?.task.idTask &&
        payload.destinationTaskIdAfter &&
        !payload.isCloserToStart
      ) {
        state.indicatorLine = {
          taskId: payload.destinationTaskIdAfter,
          top: true,
        };
        //If isCloserToStart and no task above on plan, show line at top of intersectingTask
      } else if (
        payload.isCloserToStart &&
        !payload.destinationTaskIdBefore &&
        payload.targetTaskId
      ) {
        state.indicatorLine = {
          taskId: payload.targetTaskId,
          top: true,
        };
        //Else, show line at bottom of tasks
      } else if (payload.isCloserToStart) {
        state.indicatorLine = {
          taskId: payload.destinationTaskIdBefore!,
          top: false,
        };
      } else if (payload.targetTaskId) {
        state.indicatorLine = {
          taskId: payload.targetTaskId,
          top: false,
        };
      }
    },
    setEditMode(state, action: PayloadAction<boolean>) {
      state.isDragAndDropTabOpen = action.payload;
    },
    setSessionStatus(state, action: PayloadAction<boolean>) {
      state.activeSession = action.payload;
      if (!action.payload) {
        delete state.xPosition;
        delete state.yPosition;
        state.isDragging = false;
        delete state.taskBeingDragged;
        delete state.intersectingIdTaskPlanAndPlacement;
        delete state.customGanttChartData;
        delete state.indicatorLine;
      }
    },
    setActiveSessionStatus(
      state,
      action: PayloadAction<SessionStatusResponse>,
    ) {
      state.activeSessionStatus = action.payload;
    },
    dropTask(state) {
      delete state.xPosition;
      delete state.yPosition;
      state.isDragging = false;
      state.isDragConfirmed = false;
      delete state.taskBeingDragged;
      delete state.intersectingIdTaskPlanAndPlacement;
      delete state.indicatorLine;
    },
    cancelDrag(state) {
      delete state.xPosition;
      delete state.yPosition;
      state.isDragging = false;
      state.isDragConfirmed = false;
      delete state.taskBeingDragged;
      delete state.intersectingIdTaskPlanAndPlacement;
      delete state.indicatorLine;
    },
    setTaskBeingDragged(state, action: PayloadAction<TaskBeingDraggedObject>) {
      state.taskBeingDragged = action.payload;
    },
    setCustomGanttChartData(state, action: PayloadAction<GanttChartData>) {
      state.customGanttChartData = action.payload;
    },
    deleteCustomGanttChartData(state) {
      delete state.customGanttChartData;
    },
    setRulesBroken(state, action: PayloadAction<number | undefined>) {
      state.rulesBroken = action.payload;
    },
    setTransitioning(state, action: PayloadAction<boolean>) {
      state.isTransitioning = action.payload;
    },
    setDragConfirmed(state, action: PayloadAction<boolean>) {
      state.isDragConfirmed = action.payload;
    },
    closeDragAndDropSessionWarningModal(state) {
      state.isDragAndDropSessionWarningModalOpen = false;
    },
    openDragAndDropSessionWarningModal(state) {
      state.isDragAndDropSessionWarningModalOpen = true;
    },
    setScrollPosition(state, action: PayloadAction<ScrollPosition>) {
      state.scrollPosition = action.payload;
    },
    removeScrollPosition(state) {
      delete state.scrollPosition;
    },
  },
});

export const {
  setIsDragging,
  setCoordinates,
  dropTask,
  cancelDrag,
  setTaskBeingDragged,
  setEditMode,
  setSessionStatus,
  setTaskBoxConfigurations,
  setIntersectingIdTaskPlanAndPlacement,
  setCustomGanttChartData,
  setRulesBroken,
  setTransitioning,
  setDragConfirmed,
  closeDragAndDropSessionWarningModal,
  openDragAndDropSessionWarningModal,
  setScrollPosition,
  removeScrollPosition,
  setActiveSessionStatus,
  deleteCustomGanttChartData,
} = dragAndDropSSlice.actions;

export default dragAndDropSSlice.reducer;
